/**
 * The placement target registry (owner requirement 2026-10-08, ADR 0010, design §6.4): where a new
 * tenant's Azure resources may go, read from `TENANT_RESOURCES_TARGETS` on the web, the runner and
 * the provisioner. It replaces the former `TENANT_RESOURCES_ENV`, which is the one-target case.
 *
 * A tenant records its target at creation (`resources_target_id`, `resources_subscription_id`,
 * `resources_resource_group`, write-once); everything after reads that recorded target, never the
 * default, and fails `TARGET_NOT_REGISTERED` or `TARGET_MISMATCH` rather than falling back
 * (`tenantTargetCheck`).
 *
 * Every refusal is a sentence naming the field and the target id (or its position, when the id
 * itself is the problem), never a value: the variable holds subscription ids and resource ids.
 */
import { z } from "zod";
import { NAMING_PROFILES, namingCodesProblem } from "./tenantResourceNames";
import { TENANT_RESOURCES_DEFAULT_TARGET_KEY } from "../types/tenantResources";
import { addDaysToDate, startOfLocalDate } from "../cron/cronUtils";

const GUID = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
export const TARGET_ID_PATTERN = /^[a-z0-9-]{3,32}$/;
const CODE = /^[a-z0-9]+$/;
/** Azure's resource group rule: 1 to 90 of letters, digits, `-_.()`, not ending in a period. */
const RESOURCE_GROUP = /^[-\w.()]{0,89}[-\w()]$/;
/** `/subscriptions/<guid>/resourceGroups/<group>/providers/<namespace>/<type>/<name>[/<type>/<name>]…` */
const ARM_ID =
  /^\/subscriptions\/[0-9a-f-]{36}\/resourceGroups\/[^/]+\/providers\/[a-z0-9.]+(\/[^/]+\/[^/]+)+$/i;

const armIdOf = (type: RegExp) => (value: string) => ARM_ID.test(value) && type.test(value);
const isSubnetId = armIdOf(
  /\/providers\/Microsoft\.Network\/virtualNetworks\/[^/]+\/subnets\/[^/]+$/i,
);
const isDnsZoneId = armIdOf(/\/providers\/Microsoft\.Network\/privateDnsZones\/[^/]+$/i);
const isWorkspaceId = armIdOf(/\/providers\/Microsoft\.OperationalInsights\/workspaces\/[^/]+$/i);
const isQueueId = armIdOf(
  /\/providers\/Microsoft\.Storage\/storageAccounts\/[^/]+\/queueServices\/default\/queues\/[^/]+$/i,
);

const nullableArmId = (check: (v: string) => boolean, what: string) =>
  z
    .string()
    .refine(check, `must be the ARM id of ${what}`)
    .nullable()
    .optional()
    .transform((v) => v ?? null);

const dnsZoneIdsSchema = z
  .object({
    vault: nullableArmId(isDnsZoneId, "a private DNS zone"),
    blob: nullableArmId(isDnsZoneId, "a private DNS zone"),
    /** The tenant progress queue's zone (A16, `targetNetwork.bicep` outputs it). */
    queue: nullableArmId(isDnsZoneId, "a private DNS zone"),
    // TODO(Infra Q3): the `privatelink.dfs.core.windows.net` zone; only read when enableDfsEndpoint.
    dfs: nullableArmId(isDnsZoneId, "a private DNS zone"),
  })
  .strict();

/**
 * Infra's canonical tag set (ADR 0010 condition of acceptance 1, Infra's unified tagging schema;
 * issue #869): every tenant vault, account and identity carries each of these names, from the
 * target's `tags`. `Customer` is not here: it is the target's own `customer` field (condition 2),
 * written by the provisioner, and the tenant's Name goes in the separate `Tenant` tag. `Protected`
 * is `true` (vaults and accounts are locked, so `resourceLocks` follows it). A target missing one
 * is refused at start. Azure tag names are case-insensitive, so no name here may equal a
 * `RESERVED_TAG_NAMES` name or a `skydata` name in any case, nor a `DEFAULT_TAG_NAMES` name in
 * another case (`tenantResourceTargets.test.ts`).
 */
export const CANONICAL_TAG_NAMES = [
  "Environment",
  "CostCenter",
  "ManagedBy",
  "Team",
  "Compliance",
  "Protected",
  "Project",
] as const;

/**
 * The canonical set's tag on DATA-BEARING resources only (condition 1): a canonical target carries
 * it in `tags`, and the provisioner writes it on the tenant's vault and account, never on an
 * identity (`templateTags`, `dataTemplateTags`).
 */
export const DATA_CLASSIFICATION_TAG = "DataClassification";

/**
 * The sub 4 lowercase set, accepted during the transition to the canonical set (issue #869): a
 * target deployed before #869 carries exactly these, and keeps working until its placement target
 * is redeployed from a converted environment file. A target with ANY canonical-only name
 * (`CostCenter`, `ManagedBy`, `Team`, `Compliance`, `Project`, `DataClassification`) is read as
 * canonical and needs the whole canonical set. Remove once every environment file is converted.
 */
export const LEGACY_TAG_NAMES = ["unit", "env", "cost-center", "product", "managed-by"] as const;

/** Which set a target's tags are read as (`LEGACY_TAG_NAMES`): names only, never a value. */
type TargetTagSet = "canonical" | "legacy";

/** The canonical names a legacy target never carries (`Environment` and `Protected` it may). */
const CANONICAL_ONLY = new Set<string>(
  [...CANONICAL_TAG_NAMES, DATA_CLASSIFICATION_TAG].filter(
    (n) => n !== "Environment" && n !== "Protected",
  ),
);

/**
 * Canonical when any canonical-only name is present or the legacy set is incomplete (so a target
 * with too few tags is told the canonical names it lacks); legacy only when every legacy name is
 * present and no canonical-only one is.
 */
export function targetTagSet(tags: Record<string, string>): TargetTagSet {
  const keys = Object.keys(tags);
  if (keys.some((k) => CANONICAL_ONLY.has(k))) return "canonical";
  return LEGACY_TAG_NAMES.every((n) => keys.includes(n)) ? "legacy" : "canonical";
}

/**
 * Tag names the provisioner writes itself (`buildTemplateParameters`), so a target may not set
 * them in any case: `Customer` is the `customer` field, `Tenant` the tenant's Name, `Vendor` the
 * templates' own, and the `skydata` names are the pairing's and the policies' (any spelling),
 * `skydata-tenant-name` (the Display name, `shared/azure/tenantTags.ts`) among them.
 */
export const RESERVED_TAG_NAMES = ["Customer", "Tenant", "Vendor"] as const;
const RESERVED_TAG_PREFIX = /^skydata[.-]/i;

/**
 * Tag names the provisioner writes as a default that the target may override, but only spelled
 * exactly so (`templateTags`). Azure tag names are case-insensitive: a target's `region` beside the
 * default `Region` would be one tag twice, refused or one value silently lost (the rehearsal's
 * `product=skydata` and `Product=skyData` became one `product` tag holding `skyData`).
 */
export const DEFAULT_TAG_NAMES = ["Environment", "Region"] as const;

/**
 * A role assignment every resource of a target inherits from ABOVE it, which Infra has reviewed and
 * allows (rehearsal finding F24, 2026-10-09): in Skypoint's directory every storage account inherits
 * Storage Blob Data Contributor and Reader from the tenant-root management group, to the Wiz
 * scanner's service principal. Without an entry the provisioner's `verify_grants`, its hourly sweep,
 * the runner's daily grant audit and `scripts/tenant-target.ts verify` count such a grant as
 * unexpected, as before.
 *
 * An entry admits ONE assignment: the same principal, the same role and the same scope, each
 * EXACTLY (`matchesInheritedGrant`). It is never a pattern: a management group scope admits nothing
 * at another group, the same principal with another role is still unexpected, and a tenant
 * identity's grant is never admitted by an entry (`runner/provisioner/pairing.ts`). Only a scope
 * above the target's resources can be listed: a management group, or the target's own subscription.
 * An entry may carry `expires` (#1700), after which it admits nothing.
 */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
/** `2026-02-30` is a date's shape but not a date: the day must survive a round trip. */
function isCalendarDate(v: string): boolean {
  const t = Date.parse(`${v}T00:00:00.000Z`);
  return Number.isFinite(t) && new Date(t).toISOString().slice(0, 10) === v;
}
const MANAGEMENT_GROUP_SCOPE =
  /^\/providers\/Microsoft\.Management\/managementGroups\/[-\w().]{1,90}$/i;
const SUBSCRIPTION_SCOPE =
  /^\/subscriptions\/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

export const inheritedGrantSchema = z
  .object({
    principalId: z.string().regex(GUID, "must be an object id (GUID)"),
    /** The role definition's GUID (the same in every scope for a built-in role), never its name. */
    roleDefinitionId: z.string().regex(GUID, "must be a role definition GUID"),
    scope: z
      .string()
      .refine(
        (v) => MANAGEMENT_GROUP_SCOPE.test(v) || SUBSCRIPTION_SCOPE.test(v),
        "must be a management group id (/providers/Microsoft.Management/managementGroups/<name>) or a subscription id (/subscriptions/<guid>)",
      ),
    /**
     * #1700: the last day the entry admits its grant (`YYYY-MM-DD`), for a time-boxed exception
     * (#881). From the next day the grant is reported like any unexpected grant, naming the entry as
     * expired; for the 7 days before, every reader warns (`INHERITED_GRANT_WARNING_DAYS`). Absent is
     * no expiry. The day is the owner's: it ends at midnight Pacific (`INHERITED_GRANT_EXPIRY_ZONE`,
     * owner rule on #1078: Pacific where no zone of a person is known, never UTC).
     */
    expires: z
      .string()
      .regex(ISO_DATE, "must be a date (YYYY-MM-DD)")
      .refine(isCalendarDate, "must be a real calendar date (YYYY-MM-DD)")
      .optional(),
  })
  .strict();
export type InheritedGrant = z.infer<typeof inheritedGrantSchema>;

/** #1700: how many days before its `expires` date an entry's readers start to warn. */
export const INHERITED_GRANT_WARNING_DAYS = 7;

/**
 * #1700: the zone an `expires` day is judged in. The date is set by the owner, in Pacific, and the
 * owner rule (#1078) puts Pacific where no zone of a person is known, never UTC: an entry dated
 * 2026-11-15 admits its grant until 00:00 Pacific on 2026-11-16 (08:00 UTC that day, PST).
 */
export const INHERITED_GRANT_EXPIRY_ZONE = "America/Los_Angeles";

/**
 * An entry's standing at `now`: `active` (no expiry, or more than the warning window away),
 * `expiring` (still admitted, inside the last `INHERITED_GRANT_WARNING_DAYS` days, the `expires`
 * day included: a warning, never a refusal) or `expired` (admits nothing). The `expires` day is
 * admitted to its end, midnight Pacific; the window opens at midnight Pacific six days before it.
 */
export type InheritedGrantStatus =
  | { state: "active" }
  | { state: "expiring"; expires: string }
  | { state: "expired"; expires: string };

export function inheritedGrantStatus(g: InheritedGrant, now: Date): InheritedGrantStatus {
  if (g.expires === undefined) return { state: "active" };
  const zone = INHERITED_GRANT_EXPIRY_ZONE;
  const endsAt = startOfLocalDate(addDaysToDate(g.expires, 1), zone).getTime();
  const warnsFrom = startOfLocalDate(
    addDaysToDate(g.expires, 1 - INHERITED_GRANT_WARNING_DAYS),
    zone,
  ).getTime();
  const at = now.getTime();
  if (at >= endsAt) return { state: "expired", expires: g.expires };
  if (at >= warnsFrom) return { state: "expiring", expires: g.expires };
  return { state: "active" };
}

/** One entry as a person reads it: principal, role GUID and scope, ids only. */
export function describeInheritedGrant(g: InheritedGrant): string {
  return `${g.principalId} with ${g.roleDefinitionId} at ${g.scope}`;
}

/** #1700: the sentence every reader uses for an expired entry whose grant still exists. */
export function inheritedGrantExpiredSentence(g: InheritedGrant & { expires: string }): string {
  return `Inherited grant ${describeInheritedGrant(g)} expired at the end of ${g.expires} (Pacific time) and is no longer allowed.`;
}

/** #1700: the warning inside the window; the grant is still admitted. */
export function inheritedGrantExpiringSentence(g: InheritedGrant & { expires: string }): string {
  return (
    `Inherited grant ${describeInheritedGrant(g)} is allowed only until the end of ${g.expires} (Pacific time). ` +
    "Remove the grant, or renew the entry with the owner's decision, before then."
  );
}

const ROLE_DEFINITION_ID =
  /\/providers\/Microsoft\.Authorization\/roleDefinitions\/([0-9a-fA-F-]{36})$/i;

/**
 * Whether an assignment is the one an entry names: principal, role GUID and scope, each exactly
 * (ids compare without case, as Azure does). Nothing is trimmed, widened or matched by prefix. It
 * ignores `expires`: it says which entry an assignment is, never whether the entry still admits it
 * (`matchesInheritedGrant`).
 */
export function sameInheritedGrant(
  a: { scope: string; principalId: string; roleDefinitionId: string },
  g: InheritedGrant,
): boolean {
  const role = ROLE_DEFINITION_ID.exec(a.roleDefinitionId)?.[1];
  return (
    role !== undefined &&
    role.toLowerCase() === g.roleDefinitionId.toLowerCase() &&
    a.principalId.toLowerCase() === g.principalId.toLowerCase() &&
    a.scope.toLowerCase() === g.scope.toLowerCase()
  );
}

/**
 * The one rule that ADMITS an assignment by an entry: the same grant (`sameInheritedGrant`) and an
 * entry that has not expired at `now` (#1700). `now` is required, so no reader can leave the
 * expiry out by accident.
 */
export function matchesInheritedGrant(
  a: { scope: string; principalId: string; roleDefinitionId: string },
  g: InheritedGrant,
  now: Date,
): boolean {
  return sameInheritedGrant(a, g) && inheritedGrantStatus(g, now).state !== "expired";
}

/** A target's data class (`placementTarget.bicep` `dataClass`, no default on purpose). */
export const DATA_CLASSES = ["dev", "production", "phi"] as const;
export type DataClass = (typeof DATA_CLASSES)[number];
/** Classes whose tenants' vault and account logs must go to a 365-day workspace (ADR condition 3). */
export const DATA_CLASSES_NEEDING_WORKSPACE: ReadonlySet<DataClass> = new Set([
  "production",
  "phi",
]);

/**
 * Built-in role GUIDs a target's `jobBlobRoleId` may never be (lower case): Storage Blob Data
 * Contributor and Owner (their container control actions are what R-J exists to leave out), Owner,
 * Contributor, User Access Administrator, and every built-in data role the provisioner grants or
 * expects (`ROLE` in `runner/provisioner/engine/armIds.ts`, each pinned in
 * this set by `runner/provisioner/__tests__/grantConditions.test.ts`). A registry naming one is refused at start.
 */
export const BUILT_IN_ROLES_NOT_JOB_BLOB: ReadonlySet<string> = new Set([
  "ba92f5b4-2d11-453d-a403-e96b0029c9fe", // Storage Blob Data Contributor
  "b7e6dc6d-f1e8-4753-8033-0f276bb0955b", // Storage Blob Data Owner
  "8e3af657-a8ff-443c-a75c-2fe8c4bcb635", // Owner
  "b24988ac-6180-42a0-ab88-20f7382dd24c", // Contributor
  "18d7d88d-d35e-4fb5-a5c3-7773c20a72d9", // User Access Administrator
  "4633458b-17de-408a-b874-0445c86b69e6", // Key Vault Secrets User
  "c6a89b2d-59bc-44d0-9896-0f6e12d7b80a", // Storage Queue Data Message Sender
  "2a2b9908-6ea1-4ae2-8e65-a410df84e7d1", // Storage Blob Data Reader
]);

export const tenantResourceTargetSchema = z
  .object({
    id: z.string().regex(TARGET_ID_PATTERN, "must be 3 to 32 of a-z, 0-9 and -"),
    displayName: z
      .string()
      .trim()
      .min(1, "must not be empty")
      .max(200, "must be at most 200 characters"),
    subscriptionId: z.string().regex(GUID, "must be a GUID"),
    resourceGroup: z.string().regex(RESOURCE_GROUP, "must be a resource group name"),
    location: z.string().regex(CODE, "must be an Azure region name such as westus2"),
    /** In the storage and identity names and the `Region` tag; never in the vault name (Infra Q21). */
    regionCode: z.string().regex(CODE, "must be lower-case letters and digits"),
    privateNetwork: z.boolean(),
    peSubnetId: nullableArmId(isSubnetId, "a subnet"),
    dnsZoneIds: dnsZoneIdsSchema
      .nullable()
      .optional()
      .transform((v) => v ?? null),
    enableDfsEndpoint: z.boolean().default(false),
    /**
     * Infra's canonical tags (`CANONICAL_TAG_NAMES` and `DataClassification` required), or, during the
     * transition, the sub 4 set (`LEGACY_TAG_NAMES`; `targetTagSet`), each a non-empty string.
     */
    tags: z.record(z.string(), z.string().min(1, "must be a non-empty string")),
    /**
     * The `Customer` tag on every tenant resource, EXACTLY as given (DDAZ requires `Customer=DDAZ`):
     * never trimmed or case-folded; a value with a space at either end is refused instead.
     */
    customer: z
      .string()
      .min(1, "must not be empty")
      .max(256, "must be at most 256 characters")
      .refine((v) => v.trim() === v, "must not start or end with a space"),
    dataClass: z.enum(DATA_CLASSES),
    /** The Log Analytics workspace of the tenants' diagnostics; required for production and phi. */
    // `placementTarget.bicep` writes `''` for none: the same as absent (still required below for
    // production and phi).
    diagnosticsWorkspaceId: z.preprocess(
      (v) => (v === "" ? null : v),
      nullableArmId(isWorkspaceId, "a Log Analytics workspace"),
    ),
    resourceLocks: z.boolean(),
    storageSku: z.enum(["Standard_LRS", "Standard_ZRS"]),
    softDeleteDays: z
      .number()
      .int("must be a whole number")
      .min(7, "must be 7 to 90")
      .max(90, "must be 7 to 90"),
    purgeProtection: z.boolean(),
    /** Owner recommends false; Infra decides per target (Q15, Infra change e). */
    defenderForStorageDefault: z.boolean(),
    defenderMalwareScanCapGBPerMonth: z
      .number()
      .int("must be a whole number")
      .min(0, "must be 0 or more"),
    /**
     * R-J, the tenant job identities' blob role (`infra/tenant/tenantRoleDefinitions.bicep`
     * `jobBlobRole`): a custom role per subscription, `guid(subscription().id, 'skydata-tenant-job-blob',
     * environmentName)`, so it differs per target. `placementTarget.bicep` writes it in `targetEntry`;
     * the provisioner sends it to the tenant template and the pairing checks expect it. Data
     * actions only, unlike Storage Blob Data Contributor, whose container control actions no ABAC
     * condition reaches (security review 2026-10-10).
     */
    jobBlobRoleId: z
      .guid("must be a role definition GUID")
      .transform((v) => v.toLowerCase())
      .refine(
        (v) => !BUILT_IN_ROLES_NOT_JOB_BLOB.has(v),
        "must be the custom tenant job blob role (R-J), never a built-in role",
      ),
    /**
     * Infra's reviewed grants inherited from above (`inheritedGrantSchema`); absent is none. Not a
     * `placementTarget.bicep` output: `scripts/tenant-target.ts` adds it to the entry from the
     * target's config once `verify` has checked it against the live assignments.
     */
    inheritedGrants: z.array(inheritedGrantSchema).default([]),
    /**
     * #906: the deployment's configured default target for new tenants, from the target marked
     * `default: true` in `environments/<env>.yaml` (absent is false). Not a `placementTarget.bicep`
     * output: `scripts/tenant-target.ts` adds it to the entry from the target's config, as it does
     * `inheritedGrants`. At most one target carries it; a default stored in `platform_settings`
     * still wins (`resolveDefaultTarget`).
     */
    default: z.boolean().optional(),
  })
  .strict();
export type TenantResourceTarget = z.infer<typeof tenantResourceTargetSchema>;

export const tenantResourceTargetsSchema = z
  .object({
    namingProfile: z.enum(NAMING_PROFILES),
    environmentCode: z.string().regex(CODE, "must be lower-case letters and digits"),
    customerCode: z
      .string()
      .regex(CODE, "must be lower-case letters and digits")
      .nullable()
      .optional()
      .transform((v) => v ?? null),
    // The web, runner and provisioner principals, which hold the standing grants
    // (`provisionerApp.bicep` outputs them, `release.ts` checks them against the live principals);
    // the hourly sweep and the runner's daily grant audit both exempt exactly these (F50).
    standingPrincipalIds: z.array(z.guid("must be a list of object ids (GUIDs)")),
    progressQueueId: z.string().refine(isQueueId, "must be the ARM id of the run-progress queue"),
    targets: z.array(tenantResourceTargetSchema),
  })
  .strict();
export type TenantResourceTargets = z.infer<typeof tenantResourceTargetsSchema>;

export class TenantResourceTargetsError extends Error {
  constructor(readonly problems: string[]) {
    super(`TENANT_RESOURCES_TARGETS is refused: ${problems.join(" ")}`);
    this.name = "TenantResourceTargetsError";
  }
}

/** Where an issue sits, in words that never carry a value but the (valid) target id. */
function placeOf(raw: unknown, path: PropertyKey[]): string {
  if (path[0] !== "targets" || typeof path[1] !== "number") {
    return path.length > 0 ? `the field ${path.map(String).join(".")}` : "the value";
  }
  const index = path[1];
  const rest = path.slice(2).map(String).join(".");
  const targets = (raw as { targets?: unknown[] } | null)?.targets;
  const id = (targets?.[index] as { id?: unknown } | undefined)?.id;
  const who =
    typeof id === "string" && TARGET_ID_PATTERN.test(id)
      ? `target ${id}`
      : `the target at position ${String(index + 1)}`;
  return rest ? `the field ${rest} of ${who}` : who;
}

/** A private target's own requirements: its subnet, both zones (and dfs's), purge protection. */
function privateNetworkProblems(t: TenantResourceTarget): string[] {
  if (!t.privateNetwork) return [];
  const problems: string[] = [];
  if (!t.peSubnetId) {
    problems.push(`The field peSubnetId of target ${t.id} is required on a private network.`);
  }
  if (!t.dnsZoneIds?.vault || !t.dnsZoneIds.blob || !t.dnsZoneIds.queue) {
    problems.push(
      `The fields dnsZoneIds.vault, dnsZoneIds.blob and dnsZoneIds.queue of target ${t.id} are required on a private network.`,
    );
  }
  if (t.enableDfsEndpoint && !t.dnsZoneIds?.dfs) {
    problems.push(
      `The field dnsZoneIds.dfs of target ${t.id} is required when enableDfsEndpoint is true.`,
    );
  }
  if (!t.purgeProtection) {
    problems.push(`The field purgeProtection of target ${t.id} must be true on a private network.`);
  }
  return problems;
}

/** The names of the target's set (`targetTagSet`) it leaves out. */
function missingCanonicalTagProblems(t: TenantResourceTarget, keys: string[]): string[] {
  const problems: string[] = [];
  const required =
    targetTagSet(t.tags) === "legacy"
      ? LEGACY_TAG_NAMES
      : [...CANONICAL_TAG_NAMES, DATA_CLASSIFICATION_TAG];
  for (const name of required) {
    if (!keys.includes(name)) {
      problems.push(
        `The field tags.${name} of target ${t.id} is required (Infra's canonical tags).`,
      );
    }
  }
  return problems;
}

/** Per tag name, in the target's order: reserved, a default's case variant, a case-only repeat. */
function tagNameProblems(t: TenantResourceTarget, keys: string[]): string[] {
  const problems: string[] = [];
  const seen = new Map<string, string>();
  for (const k of keys) {
    const folded = k.toLowerCase();
    const reserved = RESERVED_TAG_NAMES.some((r) => r.toLowerCase() === folded);
    if (reserved || RESERVED_TAG_PREFIX.test(k)) {
      problems.push(
        `The field tags of target ${t.id} sets a tag the provisioner writes itself (${k}); ` +
          "the Customer tag is the field customer.",
      );
    }
    const asDefault = DEFAULT_TAG_NAMES.find((d) => d.toLowerCase() === folded);
    if (asDefault !== undefined && asDefault !== k) {
      problems.push(
        `The field tags.${k} of target ${t.id} differs only in case from the provisioner's ` +
          `${asDefault} tag; Azure tag names are case-insensitive, so write it as ${asDefault}.`,
      );
    }
    const earlier = seen.get(folded);
    if (earlier !== undefined) {
      problems.push(
        `The fields tags.${earlier} and tags.${k} of target ${t.id} name one tag; ` +
          "Azure tag names are case-insensitive.",
      );
    } else {
      seen.set(folded, k);
    }
  }
  return problems;
}

/**
 * The Protected tag, when set, is true or false, and resource locks follow it. A canonical target
 * states `Protected=true` (condition 1: tenant vaults and accounts are protected and locked).
 */
function protectedTagProblems(t: TenantResourceTarget): string[] {
  const protectedTag = t.tags.Protected;
  if (protectedTag === undefined) return [];
  if (protectedTag !== "true" && protectedTag !== "false") {
    return [`The field tags.Protected of target ${t.id} must be true or false.`];
  }
  if (protectedTag !== "true" && targetTagSet(t.tags) === "canonical") {
    return [
      `The field tags.Protected of target ${t.id} must be true: tenant vaults and accounts are protected (ADR 0010 condition 1).`,
    ];
  }
  if ((protectedTag === "true") !== t.resourceLocks) {
    return [
      `The field resourceLocks of target ${t.id} must follow tags.Protected (locks exactly when Protected is true).`,
    ];
  }
  return [];
}

/** The tag rules of ADR 0010's conditions 1 to 3 (A12); names only, never a value. */
function tagProblems(t: TenantResourceTarget): string[] {
  const keys = Object.keys(t.tags);
  const problems = [
    ...missingCanonicalTagProblems(t, keys),
    ...tagNameProblems(t, keys),
    ...protectedTagProblems(t),
  ];
  if (DATA_CLASSES_NEEDING_WORKSPACE.has(t.dataClass) && !t.diagnosticsWorkspaceId) {
    problems.push(
      `The field diagnosticsWorkspaceId of target ${t.id} is required when dataClass is production or phi.`,
    );
  }
  return problems;
}

/** An inherited grant must sit above this target: its own subscription or a management group. */
function inheritedGrantProblems(t: TenantResourceTarget): string[] {
  const problems: string[] = [];
  const seen = new Set<string>();
  for (const [index, g] of t.inheritedGrants.entries()) {
    const at = `The field inheritedGrants.${String(index)} of target ${t.id}`;
    const key = [g.principalId, g.roleDefinitionId, g.scope].join("|").toLowerCase();
    if (seen.has(key)) problems.push(`${at} repeats an earlier entry.`);
    seen.add(key);
    if (
      SUBSCRIPTION_SCOPE.test(g.scope) &&
      g.scope.toLowerCase() !== `/subscriptions/${t.subscriptionId}`.toLowerCase()
    ) {
      problems.push(`${at} names a subscription other than the target's own.`);
    }
  }
  return problems;
}

/** The §6.4 start checks the schema alone cannot say; each a sentence. */
function crossChecks(registry: TenantResourceTargets): string[] {
  const problems: string[] = [];
  const customerCodeMissing = registry.namingProfile === "customer-code" && !registry.customerCode;
  if (customerCodeMissing) {
    problems.push("The field customerCode is required by the customer-code naming profile.");
  }
  const defaults = registry.targets.filter((t) => t.default === true).map((t) => t.id);
  if (defaults.length > 1) {
    problems.push(
      `The field default is true on targets ${defaults.join(", ")}; at most one target is the default.`,
    );
  }
  const seen = new Set<string>();
  for (const t of registry.targets) {
    if (seen.has(t.id)) problems.push(`The field id of target ${t.id} is used by another target.`);
    seen.add(t.id);
    problems.push(...privateNetworkProblems(t), ...tagProblems(t), ...inheritedGrantProblems(t));
    if (customerCodeMissing) continue;
    const naming = namingCodesProblem({
      profile: registry.namingProfile,
      environmentCode: registry.environmentCode,
      regionCode: t.regionCode,
      customerCode: registry.customerCode,
    });
    if (naming) problems.push(`The field regionCode of target ${t.id}: ${naming}`);
  }
  return problems;
}

/**
 * Parses and checks `TENANT_RESOURCES_TARGETS`; throws `TenantResourceTargetsError` listing every
 * problem. An empty `targets` list parses: the web refuses per-tenant mode without a target.
 */
export function parseTenantResourceTargets(json: string): TenantResourceTargets {
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    // The parser's own message can quote part of the value, so it is not passed on.
    throw new TenantResourceTargetsError(["The value is not valid JSON."]);
  }
  const parsed = tenantResourceTargetsSchema.safeParse(raw);
  if (!parsed.success) {
    throw new TenantResourceTargetsError(
      parsed.error.issues.map((issue) => {
        const message =
          issue.code === "unrecognized_keys"
            ? `has a field it does not know (${issue.keys.join(", ")})`
            : issue.message.startsWith("Invalid") || issue.message.startsWith("Too")
              ? "is missing or of the wrong type"
              : issue.message;
        const place = placeOf(raw, issue.path);
        return `${place.charAt(0).toUpperCase()}${place.slice(1)} ${message}.`;
      }),
    );
  }
  const problems = crossChecks(parsed.data);
  if (problems.length > 0) throw new TenantResourceTargetsError(problems);
  return parsed.data;
}

export function targetById(
  registry: TenantResourceTargets,
  id: string | null | undefined,
): TenantResourceTarget | undefined {
  if (!id) return undefined;
  return registry.targets.find((t) => t.id === id);
}

/**
 * #906: the deployment's configured default (the target marked `default: true`, from
 * `environments/<env>.yaml`), else the only registered target; undefined with several targets and
 * none marked. What a new tenant gets when no default is stored.
 */
export function configuredDefaultTarget(
  registry: TenantResourceTargets,
): TenantResourceTarget | undefined {
  const marked = registry.targets.find((t) => t.default === true);
  if (marked) return marked;
  const [only, ...rest] = registry.targets;
  return only && rest.length === 0 ? only : undefined;
}

/**
 * The target a new tenant goes to: the platform setting `tenant_resources.default_target` when it
 * names a registered target (a stored default always wins, so a PlatformAdmin can change it); with
 * none stored, the configured default (`configuredDefaultTarget`: the target marked `default`, or
 * the only one). Otherwise `"UNSET"`, which `POST /platform/tenants` answers with 409
 * `TENANT_RESOURCES_TARGET_UNSET`. A stored default that names no registered target is `"UNSET"`,
 * never a silent fall back to the configured one.
 */
export function resolveDefaultTarget(
  registry: TenantResourceTargets,
  settingTargetId: string | null | undefined,
): TenantResourceTarget | "UNSET" {
  if (settingTargetId) return targetById(registry, settingTargetId) ?? "UNSET";
  return configuredDefaultTarget(registry) ?? "UNSET";
}

/**
 * #906: what a deployment says at start (and the release after deploying) about its default target
 * for new tenants, or undefined when nothing is needed. A configured default (the target marked
 * `default: true`) or a single registered target needs nothing (`resolveDefaultTarget` falls back
 * to it); a stored default that names a registered target is fine. Otherwise creating a
 * tenant without naming a target answers 409 `TENANT_RESOURCES_TARGET_UNSET`, so the notice names
 * the targets (ids only, never a subscription or a tag) and how to set the default.
 */
export interface DefaultTargetNotice {
  /** `unset`: several targets and none stored. `unregistered`: the stored one is gone. */
  reason: "unset" | "unregistered";
  targetIds: string[];
  message: string;
}

/** How a PlatformAdmin sets the default; said in every notice. */
const DEFAULT_TARGET_HOW_TO_SET =
  "Mark one target default: true in environments/<env>.yaml and release, or set it as a " +
  'PlatformAdmin on Platform administration, Tenants, "Default target for new tenants" ' +
  '(PUT /api/v2/platform/tenant-resources/default-target with {"targetId": "<id>"})';

export function defaultTargetNotice(
  registry: TenantResourceTargets | undefined,
  storedTargetId: string | null | undefined,
): DefaultTargetNotice | undefined {
  const targetIds = (registry?.targets ?? []).map((target) => target.id);
  if (!registry || targetIds.length === 0) return undefined;
  const effect =
    "so creating a tenant without choosing a target answers 409 TENANT_RESOURCES_TARGET_UNSET";
  if (storedTargetId) {
    if (targetById(registry, storedTargetId)) return undefined;
    return {
      reason: "unregistered",
      targetIds,
      message:
        `The default target for new tenants (${TENANT_RESOURCES_DEFAULT_TARGET_KEY}) names ` +
        `"${storedTargetId}", which TENANT_RESOURCES_TARGETS does not register ` +
        `(registered: ${targetIds.join(", ")}), ${effect}. ${DEFAULT_TARGET_HOW_TO_SET}.`,
    };
  }
  if (configuredDefaultTarget(registry)) return undefined;
  return {
    reason: "unset",
    targetIds,
    message:
      `TENANT_RESOURCES_TARGETS registers ${targetIds.length} placement targets ` +
      `(${targetIds.join(", ")}), none is marked default and no default target for new ` +
      `tenants is stored (${TENANT_RESOURCES_DEFAULT_TARGET_KEY}), ${effect}. ` +
      `${DEFAULT_TARGET_HOW_TO_SET}.`,
  };
}

/** The placement a tenant row recorded at creation (platform 0035, write-once). */
export interface TenantTargetRow {
  resourcesTargetId: string | null;
  resourcesSubscriptionId: string | null;
  resourcesResourceGroup: string | null;
}

export type TenantTargetCheck =
  | { ok: true; target: TenantResourceTarget }
  | { ok: false; code: "TARGET_NOT_REGISTERED" | "TARGET_MISMATCH" };

/**
 * The tenant's recorded target, still registered with the same subscription and group, or the
 * refusal. Never a fallback to the default. Subscription ids and group names compare without
 * case, as Azure does.
 */
export function tenantTargetCheck(
  registry: TenantResourceTargets,
  row: TenantTargetRow,
): TenantTargetCheck {
  const target = targetById(registry, row.resourcesTargetId);
  if (!target) return { ok: false, code: "TARGET_NOT_REGISTERED" };
  const same = (a: string | null, b: string) => a !== null && a.toLowerCase() === b.toLowerCase();
  if (
    !same(row.resourcesSubscriptionId, target.subscriptionId) ||
    !same(row.resourcesResourceGroup, target.resourceGroup)
  ) {
    return { ok: false, code: "TARGET_MISMATCH" };
  }
  return { ok: true, target };
}
